"use strict";

import moment from "moment";

const WEEKDAY_COUNT = 7;

/**
 * Pure resolver for PointSell opening hours over the structured schedule models.
 * ISO weekday convention: 1=Mon .. 7=Sun (matches backend dayOfWeek).
 */
class OpeningHoursUtil {
  /**
   * The cache key of the exceptions of 1 branch in 1 window. The store action, the store getter
   * and the pages that read the cache build the key here, so they cannot drift (MOB-440).
   */
  static exceptionsKey(pointSellMssId, windowFrom, windowTo) {
    return pointSellMssId + "#" + windowFrom + "#" + windowTo;
  }

  static _intervalsOf(schedule) {
    if (!schedule || !schedule.intervals || !schedule.intervals.items) return [];
    return schedule.intervals.items;
  }

  /**
   * The covering exception that was entered last, else null.
   *
   * The rule is the MSS rule: of the exceptions whose [dateFrom, dateTo] covers the date, the
   * one with the newest `valid_from` wins (MSS `PointSellOpenHoursLookupDaoHibernate:33`,
   * `order by s.validFrom desc`, plus the comparator in
   * `PointSellOpenHoursServerServiceImpl:182-184`, which reads `validFrom` and nothing else).
   * It is not "the narrowest window wins": a 1-day correction saved before a longer window
   * loses to that window, in MSS and here alike (MOB-093, rejected 2026-09-26 on that evidence).
   *
   * MOB does not read `valid_from` — the snapshot does not carry it (it is in neither the init
   * SQL, nor the transform, nor the schema). It reads the highest `mssId`, and that matches the
   * MSS order by an invariant of the MSS sequence, not by reading validity: in
   * `MatesBaseEntityHibernateDao.save` the stamp (line 222) and the sequence draw inside
   * `super.save(entity)` (line 293) sit in the same method call, and the stamp is written only
   * while `valid_from` is still null (line 221), so an edit keeps both the id and the timestamp
   * of the row. The id comes from `point_sell_open_hours_id_point_sell_open_hours_seq` — the
   * annotated entity `PointSellOpenHours.java:75-79` declares `GenerationType.SEQUENCE`; the
   * generated `PointSellOpenHours.hbm.xml:15` carries `generator class="assigned"`, but that
   * file is not in play, because `hibernate.cfg.xml` registers 202 `mapping class` entries and
   * 0 `mapping resource` entries. For every row the MSS editor writes, a higher `mssId`
   * therefore has a later `valid_from`.
   *
   * The stamp is the application-server clock, not the database clock and not the transaction
   * start. Line 222 of `MatesBaseEntityHibernateDao` calls
   * `getDatabaseServerService().getCurrentDate()`; line 155 of the same file resolves that to
   * the Spring bean `DatabaseServerServicePoint`, whose `getCurrentDate()` returns
   * `new Date()` — the JVM clock of the MSS application node
   * (`DatabaseServerServicePoint.java:30-33`). Its siblings `getCurrentTimestamp` and
   * `getNextValueSeguence` do delegate to the `@Service` (lines 35-41), but `getCurrentDate`
   * does not, so the `SELECT current_timestamp` body in `DatabaseServerService.java:26-33` is
   * not on this path.
   *
   * The 2 orders can diverge in 4 cases, each of which needs 2 overlapping exceptions of 1
   * branch:
   * 1. an import or a script writes an explicit `valid_from` out of id order. No writer in MSS
   *    does that today — the model-328 migration writes one
   *    (`model-328-point-sell-opening-hours.sql:66-67`, `COALESCE(ps.dat_upd, NOW())`), but only
   *    onto DEFAULT rows, which never compete as exceptions.
   * 2. 2 saves land in the same millisecond and tie: `java.util.Date` has millisecond
   *    resolution, so 2 `new Date()` stamps taken inside 1 ms are equal. One transaction does
   *    not cause this — 2 saves in 1 transaction take 2 distinct stamps in save order, which is
   *    the order the sequence is drawn in. MSS has no second sort key and returns such a tie in
   *    an undefined order; here the higher `mssId` always wins. The MSS-side tie-break is
   *    MOB-298.
   * 3. 2 saves interleave inside the sub-millisecond gap between the stamp (line 222) and the
   *    sequence draw (line 293) of one `save` call: the save that stamped first can draw its id
   *    second, and the later id then carries the earlier `valid_from`. The length of the
   *    surrounding transaction is irrelevant; only this gap is.
   * 4. the clocks of 2 MSS application nodes disagree, or NTP steps one of them backwards.
   *    Because the stamp is a node's own JVM clock while the id is the database sequence, a
   *    later id can carry an earlier `valid_from` with no overlap in time at all.
   */
  static _winningException(exceptionSchedules, dateStr) {
    if (!Array.isArray(exceptionSchedules)) return null;
    let winner = null;
    for (const ex of exceptionSchedules) {
      if (!ex || ex.dateFrom == null || ex.dateTo == null) continue;
      if (ex.dateFrom <= dateStr && dateStr <= ex.dateTo) {
        if (winner === null || Number(ex.mssId) > Number(winner.mssId)) winner = ex;
      }
    }
    return winner;
  }

  static _weekdayIntervals(schedule, isoWeekday) {
    return OpeningHoursUtil._intervalsOf(schedule)
      .filter((it) => it.dayOfWeek === isoWeekday)
      .slice()
      .sort((a, b) => a.intervalNo - b.intervalNo)
      .map((it) => ({ opens: it.opens, closes: it.closes }));
  }

  /**
   * Resolve the effective opening result for a single date.
   * @returns {{closed:boolean, intervals:{opens:string,closes:string}[], source:string, exceptionName:?string}}
   */
  static resolveDay(defaultSchedule, exceptionSchedules, date) {
    const dateStr = moment(date).format("YYYY-MM-DD");
    const isoWeekday = moment(date).isoWeekday();

    const exception = OpeningHoursUtil._winningException(exceptionSchedules, dateStr);
    const winner = exception || defaultSchedule || null;

    if (winner == null) {
      return { closed: true, intervals: [], source: "NONE", exceptionName: null };
    }

    const source = exception ? "EXCEPTION" : "DEFAULT";
    const exceptionName = exception ? exception.name || null : null;

    if (winner.closedAllPeriod === true) {
      return { closed: true, intervals: [], source, exceptionName };
    }

    const intervals = OpeningHoursUtil._weekdayIntervals(winner, isoWeekday);
    if (intervals.length === 0) {
      return { closed: true, intervals: [], source, exceptionName };
    }
    return { closed: false, intervals, source, exceptionName };
  }

  static _parseTime(hhmm) {
    if (hhmm == null) return null;
    const parts = String(hhmm).split(":");
    return { hour: parseInt(parts[0], 10), minute: parseInt(parts[1] || "0", 10) };
  }

  /** First interval's opening time, or null when closed. */
  static getOpeningTime(defaultSchedule, exceptionSchedules, date) {
    const day = OpeningHoursUtil.resolveDay(defaultSchedule, exceptionSchedules, date);
    if (day.closed) return null;
    return OpeningHoursUtil._parseTime(day.intervals[0].opens);
  }

  /** Last interval's closing time (book-span), or null when closed. */
  static getClosingTime(defaultSchedule, exceptionSchedules, date) {
    const day = OpeningHoursUtil.resolveDay(defaultSchedule, exceptionSchedules, date);
    if (day.closed) return null;
    return OpeningHoursUtil._parseTime(day.intervals[day.intervals.length - 1].closes);
  }

  /** Default-only result for a weekday (ignores exceptions). */
  static _defaultWeekday(defaultSchedule, isoWeekday) {
    if (defaultSchedule == null || defaultSchedule.closedAllPeriod === true) {
      return { closed: true, intervals: [] };
    }
    const intervals = OpeningHoursUtil._weekdayIntervals(defaultSchedule, isoWeekday);
    return { closed: intervals.length === 0, intervals };
  }

  static _sameDay(a, b) {
    if (a.closed !== b.closed) return false;
    if (a.intervals.length !== b.intervals.length) return false;
    for (let i = 0; i < a.intervals.length; i++) {
      if (a.intervals[i].opens !== b.intervals[i].opens) return false;
      if (a.intervals[i].closes !== b.intervals[i].closes) return false;
    }
    return true;
  }

  /**
   * Weekly table data (Mon..Sun) with dated exception overrides inside [windowFrom, windowTo].
   * @returns {{dayOfWeek:number, closed:boolean, defaultIntervals:object[], overrides:object[]}[]}
   */
  static buildWeeklyView(defaultSchedule, exceptionSchedules, windowFrom, windowTo) {
    const rows = [];
    for (let dow = 1; dow <= WEEKDAY_COUNT; dow++) {
      const def = OpeningHoursUtil._defaultWeekday(defaultSchedule, dow);
      rows.push({ dayOfWeek: dow, closed: def.closed, defaultIntervals: def.intervals, overrides: [] });
    }

    if (windowFrom && windowTo) {
      const cursor = moment(windowFrom, "YYYY-MM-DD");
      const end = moment(windowTo, "YYYY-MM-DD");
      while (cursor.isSameOrBefore(end, "day")) {
        const dateStr = cursor.format("YYYY-MM-DD");
        const resolved = OpeningHoursUtil.resolveDay(defaultSchedule, exceptionSchedules, dateStr);
        if (resolved.source === "EXCEPTION") {
          const dow = cursor.isoWeekday();
          const def = OpeningHoursUtil._defaultWeekday(defaultSchedule, dow);
          if (!OpeningHoursUtil._sameDay(def, { closed: resolved.closed, intervals: resolved.intervals })) {
            rows[dow - 1].overrides.push({ date: dateStr, closed: resolved.closed, intervals: resolved.intervals });
          }
        }
        cursor.add(1, "day");
      }
    }
    return rows;
  }

  /** Minutes since midnight, or null. */
  static _toMinutes(hhmm) {
    const t = OpeningHoursUtil._parseTime(hhmm);
    if (t == null || isNaN(t.hour) || isNaN(t.minute)) return null;
    return t.hour * 60 + t.minute;
  }

  /** Intervals as inclusive [from, to] minute ranges; unparsable ones are dropped. */
  static _minuteRanges(intervals) {
    if (!Array.isArray(intervals)) return [];
    return intervals
      .map((iv) => ({
        from: OpeningHoursUtil._toMinutes(iv && iv.opens),
        to: OpeningHoursUtil._toMinutes(iv && iv.closes),
      }))
      .filter((r) => r.from != null && r.to != null && r.to >= r.from);
  }

  /**
   * Hours (0..23) that hold at least one open minute of `intervals`.
   * Bounds are inclusive on both ends, like the time picker's min/max, so the
   * hour of a closing time stays selectable and only whole closed gaps drop out.
   */
  static allowedHours(intervals) {
    const ranges = OpeningHoursUtil._minuteRanges(intervals);
    const hours = [];
    for (let hour = 0; hour <= 23; hour++) {
      const from = hour * 60;
      const to = from + 59;
      if (ranges.some((r) => r.from <= to && r.to >= from)) hours.push(hour);
    }
    return hours;
  }

  /** Minutes (0..59) of `hour` that fall inside an open interval. */
  static allowedMinutes(intervals, hour) {
    const ranges = OpeningHoursUtil._minuteRanges(intervals);
    if (hour == null || isNaN(hour)) return [];
    const minutes = [];
    for (let minute = 0; minute <= 59; minute++) {
      const t = hour * 60 + minute;
      if (ranges.some((r) => r.from <= t && t <= r.to)) minutes.push(minute);
    }
    return minutes;
  }

  /**
   * Is there any structured schedule to reason about at all?
   * A point sell with dated exceptions but no default header is configured, not legacy:
   * its exceptions must restrict booking the same way `resolveDay` already reads them
   * (MOB-147). Only a point sell with neither stays unrestricted.
   */
  static _hasAnySchedule(defaultSchedule, exceptionSchedules) {
    if (defaultSchedule != null) return true;
    return Array.isArray(exceptionSchedules) && exceptionSchedules.length > 0;
  }

  /**
   * Day-level predicate: is the branch open at all on `date`?
   * Without any structured schedule the restriction is skipped (legacy tenant).
   */
  static isOpenOn(defaultSchedule, exceptionSchedules, date) {
    if (!OpeningHoursUtil._hasAnySchedule(defaultSchedule, exceptionSchedules)) return true;
    return !OpeningHoursUtil.resolveDay(defaultSchedule, exceptionSchedules, date).closed;
  }

  /**
   * Is "HH:mm" inside an open interval of `date`?
   * boundary "start": opens <= t < closes (pickup while open, not at closing).
   * boundary "end":   opens <= t <= closes (return any time the door is open).
   */
  static isTimeWithinOpenIntervals(defaultSchedule, exceptionSchedules, date, hhmm, boundary) {
    if (!OpeningHoursUtil._hasAnySchedule(defaultSchedule, exceptionSchedules)) return true;
    const day = OpeningHoursUtil.resolveDay(defaultSchedule, exceptionSchedules, date);
    if (day.closed) return false;
    const t = OpeningHoursUtil._toMinutes(hhmm);
    if (t == null) return false;
    return day.intervals.some((iv) => {
      const opens = OpeningHoursUtil._toMinutes(iv.opens);
      const closes = OpeningHoursUtil._toMinutes(iv.closes);
      if (opens == null || closes == null) return false;
      return boundary === "end" ? opens <= t && t <= closes : opens <= t && t < closes;
    });
  }

  /**
   * Endpoints-open slot rule. slotStart/slotEnd are fake-UTC ISO strings
   * (local wall-clock with +00:00 offset) — parsed with moment.utc.
   * Closed gaps/days between the endpoints are irrelevant.
   */
  static isSlotAllowed(defaultSchedule, exceptionSchedules, slotStart, slotEnd) {
    if (!OpeningHoursUtil._hasAnySchedule(defaultSchedule, exceptionSchedules)) return true;
    const start = moment.utc(slotStart);
    const end = moment.utc(slotEnd);
    return (
      OpeningHoursUtil.isTimeWithinOpenIntervals(
        defaultSchedule,
        exceptionSchedules,
        start.format("YYYY-MM-DD"),
        start.format("HH:mm"),
        "start"
      ) &&
      OpeningHoursUtil.isTimeWithinOpenIntervals(
        defaultSchedule,
        exceptionSchedules,
        end.format("YYYY-MM-DD"),
        end.format("HH:mm"),
        "end"
      )
    );
  }
}

export default OpeningHoursUtil;
